import React, { useState, useEffect } from 'react';
import { Link, useParams, useHistory, Prompt } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Spinner, Form, Alert } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { toast } from 'sonner';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { getSelectedCompanyType } from '../../../../store/selectors/AuthSelectors';
import { normalizeSSN, validateSSNDate, validateSSNChecksum } from '../../../../utils/ssnUtils';
// import { validateClearingNumber, validateAccountNumber, getBankInfo } from '../../../../utils/bankAccountUtils'; // Temporarily commented out
import PageTitle from '../../../layouts/PageTitle';
import {
  User,
  Mail,
  Phone,
  CreditCard,
  // Building2, // Temporarily unused
  MapPin,
  ArrowLeft,
  Save,
  AlertTriangle,
  CheckCircle,
  // Calendar, // Temporarily unused
  // Briefcase, // Temporarily unused
  X
} from 'lucide-react';

// Move InputField component outside to prevent re-creation on each render
const InputField = ({
  icon: Icon,
  label,
  name,
  type = 'text',
  required = false,
  placeholder = '',
  description = null,
  options = null,
  formik,
  showValidation
}) => (
  <div className="input-group">
    <div className="input-label-container">
      <label className="input-label">
        {label}
        {required && <span className="required-indicator">*</span>}
      </label>
      {description && (
        <p className="input-description">{description}</p>
      )}
    </div>
    <div className="input-container">
      <div className="input-icon">
        <Icon size={16} />
      </div>
      {options ? (
        <Form.Select
          name={name}
          value={formik.values[name]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          className={`modern-input ${formik.touched[name] && formik.errors[name] ? 'error' : ''}`}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      ) : (
        <Form.Control
          type={type}
          name={name}
          value={formik.values[name]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={placeholder}
          className={`modern-input ${formik.touched[name] && formik.errors[name] ? 'error' : ''}`}
        />
      )}
    </div>
    {showValidation && formik.touched[name] && formik.errors[name] && (
      <div className="error-message">
        <AlertTriangle size={14} />
        <span>{formik.errors[name]}</span>
      </div>
    )}
  </div>
);

const NewEmployee = () => {
  const { companyId } = useParams();
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessfullySubmitted, setIsSuccessfullySubmitted] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  // const [identifiedBank, setIdentifiedBank] = useState(null); // Temporarily commented out

  // Surface an Anställningsroll selector for any juridisk person that can
  // have styrelse / VD-roll requiring BAS class 72 routing (7220 företags-
  // ledare, 7240 styrelsearvoden). Per BAS-praxis this includes
  // Aktiebolag (ABL 8 kap), Ekonomisk förening (LEF 7 kap), Ideell
  // förening (case-law board structure), and Stiftelse (SL 2 kap
  // styrelseledamot). HB/KB/EF use partner / owner constructs (resp.
  // bolagsman / näringsidkare), which aren't employee-roles; for those
  // the selector stays hidden. Backend `_buildSalaryDebits` is universally
  // composite (not gated on companyType), so seeded charts that lack
  // 7220/7240 collapse to 7210 — see reference_bas_salary_routing.md.
  const companyType = useSelector(getSelectedCompanyType);
  const ROLE_SELECTOR_TYPES = new Set([
    'Aktiebolag',
    'Ekonomisk förening',
    'Ideell förening',
    'Stiftelse',
  ]);
  const showRoleSelector = ROLE_SELECTOR_TYPES.has(companyType);

  const formik = useFormik({
    initialValues: {
      // Employee Details
      firstname: '',
      lastname: '',
      ssn: '',
      jobtitle: '',
      clearingnumber: '',
      bankaccountnumber: '',
      phonenumber: '',
      email: '',
      address: '',
      postnummer: '',
      ort: '',
      active: true,
      employee_role: 'regular',
    },
    validationSchema: Yup.object({
      // Employee Details Validation
      firstname: Yup.string().required('Förnamn är obligatoriskt'),
      lastname: Yup.string().required('Efternamn är obligatoriskt'),
      ssn: Yup.string()
        .test('ssn-format', 'Personnummer måste vara i format ÅÅMMDD-XXXX eller ÅÅÅÅMMDD-XXXX',
          value => {
            if (!value) return false;
            const clean = value.replace(/\D/g, '');
            return clean.length === 10 || clean.length === 12;
          }
        )
        .test('ssn-date', 'Personnummer innehåller ett ogiltigt datum',
          value => {
            if (!value) return false;
            return validateSSNDate(value);
          }
        )
        .test('ssn-checksum', 'Ogiltigt personnummer (kontrollsiffran stämmer inte)',
          value => {
            if (!value) return false;
            return validateSSNChecksum(value);
          }
        )
        .required('Personnummer är obligatoriskt'),
      // jobtitle: Yup.string().required('Jobbtitel är obligatoriskt'), // Temporarily commented out
      // clearingnumber: Yup.string() // Temporarily commented out
      //   .test('clearing-format', 'Clearingnummer måste vara 4-5 siffror',
      //     value => {
      //       if (!value) return false;
      //       return /^\d{4,5}$/.test(value.replace(/[\s-]/g, ''));
      //     }
      //   )
      //   .test('clearing-valid', function (value) {
      //     if (!value) return true;
      //     const result = validateClearingNumber(value);
      //     return result.valid || this.createError({ message: result.message });
      //   })
      //   .required('Clearingnummer är obligatoriskt'),
      // bankaccountnumber: Yup.string() // Temporarily commented out
      //   .test('account-format', 'Kontonummer måste vara 6-10 siffror',
      //     value => {
      //       if (!value) return false;
      //       const clean = value.replace(/[\s-]/g, '');
      //       return /^\d{6,10}$/.test(clean);
      //     }
      //   )
      //   .test('account-valid', function (value) {
      //     if (!value) return true;
      //     const clearing = this.parent.clearingnumber;
      //     if (!clearing) return true; // Skip if clearing not entered yet
      //     const result = validateAccountNumber(clearing, value);
      //     return result.valid || this.createError({ message: result.message });
      //   })
      //   .required('Bankkonto är obligatoriskt'),
      phonenumber: Yup.string()
        .matches(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/, 'Ogiltigt telefonnummer')
        .required('Telefonnummer är obligatoriskt'),
      email: Yup.string().email('Ogiltig e-postadress').required('E-post är obligatoriskt'),
      address: Yup.string().required('Adress är obligatoriskt'),
      postnummer: Yup.string()
        .test('postal-code-format', 'Postnummer måste vara i format XXXXX eller XXX XX',
          value => {
            if (!value) return false;
            // Remove spaces and check if it's 5 digits
            const cleaned = value.replace(/\s/g, '');
            return /^\d{5}$/.test(cleaned);
          }
        )
        .required('Postnummer är obligatoriskt'),
      ort: Yup.string().required('Ort är obligatoriskt'),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setShowValidation(true);

      try {
        const createEmployeeApiUrl = getApiUrl(`${companyId}/hr/employees`);

        // Normalize SSN and postal code before sending
        const employeeDetails = {
          ...values,
          ssn: normalizeSSN(values.ssn),
          postnummer: values.postnummer.replace(/\s/g, '') // Remove spaces from postal code
        };

        const response = await axios.post(
          createEmployeeApiUrl,
          { employeeDetails },
          { withCredentials: true }
        );

        if (response.data.status === 'success') {
          setIsSuccessfullySubmitted(true);
          toast.success('Den anställda har skapats');
          history.push(`/${companyId}/employees`);
        }
      } catch (error) {
        console.error('Error:', error);
        const errorMessage = error.response?.data?.message || 'Ett fel inträffade vid sparande av anställd.';
        toast.error(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Handle form dirty state for navigation warnings
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (formik.dirty && !isSuccessfullySubmitted) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formik.dirty, isSuccessfullySubmitted]);

  // Identify bank when clearing number changes (only when complete)
  // Temporarily commented out since bank account fields are hidden
  // useEffect(() => {
  //   const clearingValue = formik.values.clearingnumber;
  //   if (clearingValue) {
  //     const cleanValue = clearingValue.replace(/\D/g, '');
  //     if (cleanValue.length >= 4 && cleanValue.length <= 5) {
  //       const bankInfo = getBankInfo(clearingValue);
  //       if (bankInfo) {
  //         setIdentifiedBank(bankInfo.bank);
  //       } else {
  //         setIdentifiedBank(null);
  //       }
  //     } else {
  //       setIdentifiedBank(null);
  //     }
  //   } else {
  //     setIdentifiedBank(null);
  //   }
  // }, [formik.values.clearingnumber]);

  const handleCancel = async () => {
    if (formik.dirty && !isSuccessfullySubmitted) {
      const result = await Swal.fire({
        title: 'Är du säker?',
        text: 'Du har osparade ändringar som kommer att gå förlorade!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ja, avbryt!',
        cancelButtonText: 'Nej, fortsätt redigera'
      });

      if (result.isConfirmed) {
        history.push(`/${companyId}/employees`);
      }
    } else {
      history.push(`/${companyId}/employees`);
    }
  };

  const isFormReadyToSubmit = () => {
    const requiredFields = [
      'firstname', 'lastname', 'ssn', // 'jobtitle', 'clearingnumber', 'bankaccountnumber', // Temporarily removed
      'phonenumber', 'email', 'address',
      'postnummer', 'ort'
    ];

    // Check all required fields are filled
    const allFieldsFilled = requiredFields.every(field => {
      const value = formik.values[field];
      return value !== null && value !== undefined && value !== '';
    });

    // Check for validation errors
    const noErrors = Object.keys(formik.errors).length === 0;

    return allFieldsFilled && noErrors;
  };


  const hasErrors = Object.keys(formik.errors).length > 0;

  return (
    <>
      <style>{`
        .new-employee-container {
          background: #f8fafc;
          min-height: 100vh;
          padding: 2rem 0;
        }
        
        .page-header {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }
        
        .back-button {
          color: #6b7280 !important;
          text-decoration: none !important;
          transition: all 0.2s ease;
          padding: 0.5rem 0.75rem;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          background: white;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
        }
        
        .back-button:hover {
          color: #374151 !important;
          background: #f9fafb !important;
          border-color: #d1d5db;
        }
        
        .form-card {
          background: white;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
          overflow: hidden;
        }
        
        .form-section {
          padding: 2rem;
        }
        
        .section-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          align-items: start;
        }
        
        .input-group {
          display: flex;
          flex-direction: column;
        }
        
        .input-label-container {
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          margin-bottom: 0.5rem;
        }
        
        .input-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin: 0 0 0.25rem 0;
          display: flex;
          align-items: center;
          line-height: 1.2;
        }
        
        .required-indicator {
          color: #ef4444;
          margin-left: 0.25rem;
        }
        
        .input-description {
          font-size: 0.75rem;
          color: #6b7280;
          margin: 0;
          line-height: 1.3;
          height: 16px;
        }
        
        .input-container {
          position: relative;
          display: flex;
          align-items: center;
          flex: 1;
        }
        
        .input-icon {
          position: absolute;
          left: 0.75rem;
          z-index: 1;
          color: #6b7280;
          pointer-events: none;
          display: flex;
          align-items: center;
        }
        
        .modern-input {
          width: 100%;
          padding: 0.75rem 0.75rem 0.75rem 2.5rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.875rem;
          background: white;
          color: #374151;
          transition: all 0.2s ease;
        }
        
        .modern-input:focus {
          outline: none;
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        
        .modern-input.error {
          border-color: var(--bs-danger);
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }
        
        .error-message {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #ef4444;
          font-size: 0.75rem;
          font-weight: 500;
          margin-top: 0.25rem;
          min-height: 20px;
        }
        
        .checkbox-group {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          margin-top: 1rem;
        }
        
        .checkbox-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
          margin: 0;
          display: flex;
          align-items: center;
        }
        
        .checkbox-group .form-check-input {
          width: 18px;
          height: 18px;
          border: 2px solid #d1d5db;
          border-radius: 4px;
          background: white;
          cursor: pointer;
          transition: all 0.2s ease;
          margin: 0;
        }
        
        .checkbox-group .form-check-input:checked {
          background-color: var(--bs-primary);
          border-color: var(--bs-primary);
        }
        
        .checkbox-group .form-check-input:focus {
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          border-color: var(--bs-primary);
        }
        
        .validation-summary {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 2rem;
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
        }
        
        .validation-icon {
          color:var(--bs-danger);
          flex-shrink: 0;
          margin-top: 0.125rem;
        }
        
        .validation-content {
          flex: 1;
        }
        
        .validation-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--bs-danger);
          margin-bottom: 0.5rem;
        }
        
        .validation-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .validation-item {
          font-size: 0.75rem;
          color: #dc2626;
          margin-bottom: 0.25rem;
        }
        
        .form-info {
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
        }
        
        .info-icon {
          color: var(--bs-primary);
          flex-shrink: 0;
          margin-top: 0.125rem;
        }
        
        .info-text {
          font-size: 0.875rem;
          color: #0c4a6e;
          margin: 0;
        }
        
        .action-buttons {
          background: white;
          border-radius: 12px;
          padding: 1.5rem 2rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: sticky;
          bottom: 2rem;
          z-index: 10;
        }
        
        .action-button {
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
        }
        
        .btn-cancel {
          background: #f1f5f9;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }
        
        .btn-cancel:hover {
          background: #e2e8f0;
          color: #475569;
        }
        
        .btn-save {
          background: var(--bs-primary);
          color: white;
        }
        
        .btn-save:hover {
          background: #2563eb;
        }
        
        .btn-save:disabled {
          background: var(--bs-secondary);
          cursor: not-allowed;
        }
        
        .loading-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* Responsive design */
        @media (max-width: 768px) {
          .new-employee-container {
            padding: 1rem 0;
          }
          
          .page-header,
          .form-section {
            padding: 1.5rem;
          }
          
          .form-grid {
            grid-template-columns: 1fr;
          }
          
          .input-group {
            min-height: auto;
          }
          
          .input-label-container {
            min-height: auto;
          }
          
          .action-buttons {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }
          
          .action-button {
            justify-content: center;
          }
        }
      `}</style>

      <PageTitle activeMenu="Lägg till anställd" motherMenu="Personal" />

      <Prompt
        when={formik.dirty && !isSuccessfullySubmitted}
        message="Du har osparade ändringar. Är du säker på att du vill lämna?"
      />

      <div className="new-employee-container">

        {/* Page Header */}
        <div className="page-header">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <Link
                to={`/${companyId}/employees`}
                className="back-button me-4"
              >
                <ArrowLeft size={16} />
                Tillbaka till personal
              </Link>
              <div>
                <h1 className="mb-1" style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b' }}>
                  Lägg till anställd
                </h1>
                <p className="mb-0" style={{ color: '#64748b', fontSize: '0.875rem' }}>
                  Skapa en ny anställd med grundläggande personuppgifter. Anställningskontrakt skapas separat.
                </p>
              </div>
            </div>
          </div>
        </div>

        <Form onSubmit={formik.handleSubmit}>
          {/* Info message */}
          <div className="form-info">
            <CheckCircle size={16} className="info-icon" />
            <p className="info-text">
              Obligatoriska fält är markerade med *. Efter att anställd är skapad kan du skapa anställningskontrakt separat. All information hanteras säkert enligt GDPR.
            </p>
          </div>

          {/* Validation Summary */}
          {showValidation && hasErrors && (
            <div className="validation-summary">
              <AlertTriangle size={16} className="validation-icon" />
              <div className="validation-content">
                <div className="validation-title">
                  Åtgärda följande fel innan du fortsätter:
                </div>
                <ul className="validation-list">
                  {Object.values(formik.errors).map((error, index) => (
                    <li key={index} className="validation-item">• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Personal Information */}
          <div className="form-card">
            <div className="form-section">
              <div className="section-title">
                <User size={20} />
                Personuppgifter
              </div>

              <div className="form-grid">
                <InputField
                  icon={User}
                  label="Förnamn"
                  name="firstname"
                  required={true}
                  placeholder="Ange förnamn"
                  formik={formik}
                  showValidation={showValidation}
                />

                <InputField
                  icon={User}
                  label="Efternamn"
                  name="lastname"
                  required={true}
                  placeholder="Ange efternamn"
                  formik={formik}
                  showValidation={showValidation}
                />

                <InputField
                  icon={CreditCard}
                  label="Personnummer"
                  name="ssn"
                  required={true}
                  placeholder="ÅÅMMDDXXXX"
                  formik={formik}
                  showValidation={showValidation}
                />

                {/* Temporarily commented out - will be added back in the future */}
                {/* <InputField
                  icon={Building2}
                  label="Jobbtitel"
                  name="jobtitle"
                  required={true}
                  placeholder="Ange jobbtitel"
                  formik={formik}
                  showValidation={showValidation}
                /> */}

                {showRoleSelector && (
                  <div className="modern-form-group" data-testid="employee-role-selector">
                    <label className="modern-label">Anställningsroll</label>
                    <div className="input-wrapper">
                      <Form.Select
                        name="employee_role"
                        value={formik.values.employee_role}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="modern-input"
                      >
                        <option value="regular">Vanlig anställd (BAS 7210)</option>
                        <option value="company_director">Företagsledare (BAS 7220)</option>
                        <option value="board_member">Styrelseledamot — arvode (BAS 7240)</option>
                      </Form.Select>
                    </div>
                    <small className="text-muted">
                      Styr vilket lönekonto som debiteras vid lönekörningens verifikat. Källa: ABL 8 kap, BAS 2024/2025.
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="form-card">
            <div className="form-section">
              <div className="section-title">
                <Mail size={20} />
                Kontaktuppgifter
              </div>

              <div className="form-grid">
                <InputField
                  icon={Mail}
                  label="E-postadress"
                  name="email"
                  type="email"
                  required={true}
                  placeholder="namn@exempel.se"
                  formik={formik}
                  showValidation={showValidation}
                />

                <InputField
                  icon={Phone}
                  label="Telefonnummer"
                  name="phonenumber"
                  required={true}
                  placeholder="+46 XX XXX XX XX"
                  formik={formik}
                  showValidation={showValidation}
                />
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="form-card">
            <div className="form-section">
              <div className="section-title">
                <MapPin size={20} />
                Adressuppgifter
              </div>

              <div className="form-grid">
                <InputField
                  icon={MapPin}
                  label="Adress"
                  name="address"
                  required={true}
                  placeholder="Gatunamn XX"
                  formik={formik}
                  showValidation={showValidation}
                />

                <InputField
                  icon={MapPin}
                  label="Postnummer"
                  name="postnummer"
                  required={true}
                  placeholder="XXXXX"
                  formik={formik}
                  showValidation={showValidation}
                />

                <InputField
                  icon={MapPin}
                  label="Ort"
                  name="ort"
                  required={true}
                  placeholder="Ortnamn"
                  formik={formik}
                  showValidation={showValidation}
                />
              </div>
            </div>
          </div>

          {/* Bank Information - Temporarily commented out (available in employee detail view) */}
          {/* <div className="form-card">
            <div className="form-section">
              <div className="section-title">
                <CreditCard size={20} />
                Bankuppgifter
              </div>

              <div className="form-grid">
                <div>
                  <InputField
                    icon={CreditCard}
                    label="Clearingnummer"
                    name="clearingnumber"
                    required={true}
                    placeholder="XXXX"
                    formik={formik}
                    showValidation={showValidation}
                  />
                  {identifiedBank && !formik.errors.clearingnumber && formik.values.clearingnumber && (
                    <div style={{
                      marginTop: '4px',
                      fontSize: '0.75rem',
                      color: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <CheckCircle size={12} />
                      <span>{identifiedBank}</span>
                    </div>
                  )}
                </div>

                <InputField
                  icon={CreditCard}
                  label="Kontonummer"
                  name="bankaccountnumber"
                  required={true}
                  placeholder="XXXXXXXXX"
                  formik={formik}
                  showValidation={showValidation}
                />
              </div>
            </div>
          </div> */}

          {/* Status */}
          <div className="form-card">
            <div className="form-section">
              <div className="section-title">
                <CheckCircle size={20} />
                Status
              </div>

              <div className="checkbox-group">
                <Form.Check
                  type="checkbox"
                  name="active"
                  checked={formik.values.active}
                  onChange={formik.handleChange}
                />
                <label className="checkbox-label">
                  Aktiv anställd
                </label>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
              * Obligatoriska fält
            </div>
            <div className="d-flex gap-3">
              <button
                type="button"
                className="action-button btn-cancel"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                <X size={16} />
                Avbryt
              </button>
              <button
                type="submit"
                className="action-button btn-save"
                disabled={isSubmitting || !isFormReadyToSubmit()}
              >
                {isSubmitting ? (
                  <>
                    <div className="loading-spinner"></div>
                    Sparar...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Skapa anställd
                  </>
                )}
              </button>
            </div>
          </div>
        </Form>
      </div>
    </>
  );
};

export default NewEmployee;
